import { useCallback, useEffect, useState, useRef } from "react";

import clsx from "clsx";

// MUI
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { Grid, Button, Slider, TextField, FormControl, Box } from "@mui/material";

// Hooks
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";

// Helpers
import RowFeedback from "@Common/helpers/RowFeedback";
import { isNumeric } from "@/Common/helpers/Verifications/numbers";
import { getFormatAmount } from "@Common/helpers/number";
import { and, or } from "@/Common/helpers/sonar";

// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";

// Constants
import { DEFAULT_HEADER_HEIGHT, DEFAULT_ROW_HEIGHT } from "@/Washouts/constants";

// Icons
import leftArrowIcon from "@/assets/icons/dataPages/oem/left-arrow-gray.svg";
import rightArrowIcon from "@/assets/icons/dataPages/oem/right-arrow-gray.svg";
import editGray from "@/assets/icons/dataPages/oem/editGray.svg";
import editWhite from "@/assets/icons/dataPages/oem/editWhite.svg";
import CancelIcon from "@mui/icons-material/Close";
import { SaveWhiteIconComponent } from "@/Layouts/components/Tables/DataSmartTable/additional/SaveWhiteIconComponent";

// Styles
import "@DataPages/components/OEM/Tables/OemDataTable/OemDataTable.css";

const enterKeyCode = 13;
const defaultWidth = 25;

const initialCoordinates = {
	rowIndex: 0,
	colIndex: 0,
	colNumber: 0,
};

const defaultRowProps = {
	fontSize: "12px",
	fontFamily: "PTSansProNarrow",
	fontWeight: 400,
};

const hasDecimals = (str, minDecimals = 0, maxDecimals = 0) => {
	const regex = new RegExp(`^(?:[0-9]+(\\.[0-9]{${minDecimals},${maxDecimals}})?)?$`, "i");
	return regex.test(str);
};

const isInRange = (number, min, max) => min <= number && max >= number;

const getTreeDataPath = (row) => row.hierarchy;

function InputEditCell(inputProps) {
	const { id, field, error, formattedValue, rows, apiRef, parentsProps, onUpdateCellTableValue } = inputProps;
	const [value, setValue] = useState(formattedValue?.cellValue ?? "");
	const minDecimals = formattedValue?.decimals ? 1 : 0;
	const isNumber = hasDecimals(value, minDecimals, formattedValue?.decimals);

	const handleChange = (event) => {
		const inputValue = event.target.value;
		const isRange = formattedValue.columnEdit?.inputRanges
			? isInRange(inputValue, formattedValue.columnEdit.inputRanges.min, formattedValue.columnEdit.inputRanges.max)
			: true;

		if (!hasDecimals(inputValue, 0, formattedValue?.decimals) || !isRange) {
			event.preventDefault();
		} else {
			const row = rows.find((rowElem) => rowElem._id === id);
			const cellValue = row?.[field] ?? {};
			apiRef.current.setEditCellValue({ id, field, value: { ...cellValue, cellValue: inputValue }, debounceMs: 200 });
			setValue(inputValue);
		}
	};

	const successUpdateCallback = () => {
		// TODO: remove this when the subscription is implemented
		setTimeout(() => {
			RowFeedback.skeleton(id, apiRef).success();
		}, 1000);
	};

	const errorUpdateCallback = () => {
		apiRef.current.setCellMode(id, field, "edit");
		RowFeedback.skeleton(id, apiRef).success();
	};

	const handleOnBlur = async () => {
		const isRange = formattedValue.columnEdit?.inputRanges
			? isInRange(value, formattedValue.columnEdit.inputRanges.min, formattedValue.columnEdit.inputRanges.max)
			: true;

		const multiEdition = isMultiCellEdition();

		if (and(isNumber, isRange, value !== formattedValue?.cellValue, !multiEdition)) {
			const parentsPropsToUse = { ...parentsProps, parents: [...parentsProps.parents, id], columnId: field, value };
			RowFeedback.skeleton(id, apiRef).display();
			if (onUpdateCellTableValue) {
				const { locationOEMs, ...input } = parentsPropsToUse;
				const inputData = {
					locationId: input.locationId,
					commissionCycleId: input.commissionCycleId,
					franchise: input.franchise,
					oemReference: input.oemReference,
					dealerCodeFS: input.dealerCodeFS,
					metrics: [
						{
							parents: input.parents,
							update: {
								value: Number(input?.value ?? 0),
								columnId: field,
							},
						},
					],
				};
				onUpdateCellTableValue(inputData, successUpdateCallback, errorUpdateCallback);
			}
		} else if (multiEdition === "actions") {
			const parentsPropsToUse = { ...parentsProps, parents: [...parentsProps.parents, id], columnId: field, value };
			const { locationOEMs, ...input } = parentsPropsToUse;
			const row = apiRef.current.getRow(id);
			row[field]["cellValue"] = Number(input?.value ?? 0);
			row[field]["input"] = input;
			apiRef.current.setRows([row]);
		}
	};

	const isMultiCellEdition = () => {
		const allColumns = apiRef.current.getAllColumns();
		const fieldNames = allColumns?.map((column) => column.field);

		return fieldNames.find((fieldName) => fieldName === "actions") || null;
	};

	const handleOnKeyDown = (event) => {
		if (event.keyCode === enterKeyCode) {
			handleOnBlur();
		}
	};

	return (
		<FormControl fullWidth sx={{ marginTop: "5px" }}>
			<TextField
				variant="outlined"
				value={value}
				className="oem-data-text-input-table"
				onChange={handleChange}
				onBlur={handleOnBlur}
				onKeyDown={handleOnKeyDown}
				error={!isNumber || error}
			/>
		</FormControl>
	);
}

const getEditModeAndHandler = (props) => {
	const editMode = props?.content?.tableInformation?.multipleEdition ? "row" : "cell";
	const maxRows = props?.content?.properties?.isScrollChild ? 1 : undefined;
	return { editMode, maxRows };
};

const getOnCellDoubleClickHandler = (multipleEdition, handleCellDoubleClick) => (multipleEdition ? handleCellDoubleClick : () => {});

export default function OemDataTable(props) {
	const apiRef = useGridApiRef();
	const { isPresentMultipleAccess } = useAuthorization();
	const [columns, setColumns] = useState([]);
	const [columnGroupingModel, setColumnGroupingModel] = useState([]);
	const [isColumnGrouping, setIsColumnGrouping] = useState(false);
	const [rows, setRows] = useState([]);
	const [collapseDepth, setCollapseDepth] = useState(0);
	const [totalSteps, setTotalSteps] = useState(0);
	const [coordinates, setCoordinates] = useState(initialCoordinates);
	const { editMode, maxRows } = getEditModeAndHandler(props);
	const originalRowDataRef = useRef({});

	const renderValue = ({ str, styles, type, editable, isRowFooter, decimals = 0 }) => {
		if (and(["number", "percentage", "currency"].includes(type), !isNumeric(str))) {
			type = "";
		}

		if (and(editable, !str)) {
			const editIcon = isRowFooter ? editWhite : editGray;
			return (
				<Box className="!w-full !h-full !flex !items-center !justify-end">
					<span title="" id="img-converted" className="!flex !cursor-pointer">
						<img src={editIcon} alt="icon" style={{ height: "14px" }} />
					</span>
				</Box>
			);
		}

		switch (type) {
			case "number":
				return (
					<span title="" id="text-converted" style={styles}>
						{getFormatAmount(str, decimals)}
					</span>
				);
			case "percentage":
				return (
					<span title="" id="text-converted" style={styles}>
						{`${getFormatAmount(str, decimals)}%`}
					</span>
				);
			case "currency":
				return (
					<span title="" id="text-converted" style={styles}>
						{`$${getFormatAmount(str, decimals)}`}
					</span>
				);
			default:
				return <span title="" id="text-converted" style={styles} dangerouslySetInnerHTML={{ __html: str }}></span>;
		}
	};

	const generateColumnDefinition = ({ headerClassName, element, isHidden, hasEditableAccess, needBorderRight }) => ({
		headerClassName,
		field: `${element._id}`,
		align: element.styles?.align ?? "left",
		sortable: false,
		headerAlign: element.styles?.align ?? "left",
		hidden: isHidden,
		hide: isHidden,
		editable: hasEditableAccess,
		flex: 1,
		isStep: element.isStep,
		styles: {
			...(element.columnNumber ? defaultRowProps : {}),
			color: element.styles?.color,
		},
		colSpan: (value) => value?.properties?.colSpan ?? 1,
		renderEditCell: (params) => (
			<InputEditCell
				{...params}
				rows={rows}
				apiRef={apiRef}
				parentsProps={props.parentsProps}
				onUpdateCellTableValue={props.onUpdateCellTableValue}
			/>
		),
		renderHeader: () => (
			<Grid>
				<Grid
					className="column-header-title"
					sx={{ width: element.headerStyles?.width ?? "100%" }}
					style={{ ...element.headerStyles }}
				>
					{element.lineNumber ?? element.title ?? ""}
				</Grid>
			</Grid>
		),
		renderCell: ({ formattedValue, row, colDef }) =>
			renderValue({
				str: formattedValue?.cellValue,
				styles: { ...row.styles, ...colDef.styles, ...formattedValue?.styles },
				type: formattedValue?.properties?.type ?? element.columnType,
				editable: and(hasEditableAccess, !formattedValue?.noEditable),
				isRowFooter: row.rowProperties?.["is-row-footer"],
				decimals: formattedValue?.decimals ?? element.decimals,
			}),
		cellClassName: ({ row, formattedValue, colDef }) =>
			clsx({
				"!text-clip": true,
				"!pl-0": colDef.align === "right",
				"!pr-0": colDef.align === "left",
				...(formattedValue?.removeRowProperties ? [] : row.rowProperties ?? []),
				...(formattedValue?.properties ?? []),
				...{ "border-right": and(needBorderRight, !row.noBordered) },
				...{
					"background-white": row?.styles?.backgroundColor === "white" || formattedValue?.styles?.backgroundColor === "white",
				},
				...{
					"justify-content-center": formattedValue?.styles?.justifyContent === "center",
				},
				...formattedValue?.classes,
			}),
	});

	const buildColumn = (element, needBorderRight) => {
		needBorderRight = needBorderRight || element.properties?.needBorderRight;
		const headerClassName = clsx({
			"table-header-top-aligned table-data-header": true,
			"is-column-number": and(element.columnNumber, !!element.lineNumber),
			"border-right": and(needBorderRight, !element.properties?.removeBorderRight),
			"header-overflow-initial": element.headerStyles?.overflowInitial,
		});

		const hasEditableActions = !!element.columnEdit && !!element.columnEdit.actions?.length;
		const isDealerCodeFSValid =
			props?.parentsProps?.locationOEMs?.some((oem) => oem.dealerCodeFS === props?.parentsProps?.dealerCodeFS) || false;
		const hasEditableAccess = and(hasEditableActions, isDealerCodeFSValid, isPresentMultipleAccess(element.columnEdit?.actions ?? []));
		const isHidden = or(Boolean(element.properties?.hidden), Boolean(element.properties?.hide));

		const column = generateColumnDefinition({ headerClassName, element, isHidden, hasEditableAccess, needBorderRight });
		if (element.styles?.width) {
			column.flex = 0;
			column["width"] = element.styles.width;
			column["minWidth"] = element.styles.width;
		}
		if (element.styles?.minWidth) {
			column["minWidth"] = element.styles.minWidth;
		}
		return column;
	};

	const buildColumnGroupingModel = (data) => {
		let isGrouping = false;
		const headerColumns = [];
		const currentColumns = [];

		const getColumnGroupingModel = (columnData, noBordered, isLastGroup) => {
			const noTitles = columnData.every((element) => !element.title);
			return columnData?.map((element, index) => {
				const isLastElement = isLastGroup && index + 1 === columnData.length;
				const needBorderRight = !element.properties?.noBordered && index + 1 === columnData.length;

				if (element.sectionType === "COLUMN_GROUP") {
					isGrouping = true;
					noBordered = Boolean(element.properties?.noBordered);
					const headerNoBordered = !!element.headerProperties?.noBordered && element.headerProperties?.noBordered;

					return {
						groupId: element._id,
						headerName: element.title || "",
						description: "",
						lineNumber: element.lineNumber,
						headerClassName: `table-data-header column-group-header${
							or(element.properties?.needBorderRight, and(!isLastElement, !noBordered, !headerNoBordered, !noTitles))
								? " border-right"
								: ""
						}`,
						renderHeaderGroup: (params) => {
							return (
								<Grid container>
									<Grid item className="column-header-line-number" width={element.lineNumberWidth}>
										{element.lineNumber}
									</Grid>
									<Grid item className="column-header-title" style={{ ...element.styles }}>
										<Grid item className="column-header-title-container" style={{ ...element.styles }}>
											<span
												title=""
												id="text-converted"
												dangerouslySetInnerHTML={{ __html: params.headerName }}
											></span>
										</Grid>
									</Grid>
								</Grid>
							);
						},
						children: getColumnGroupingModel(element.columns, noBordered, isLastElement),
					};
				}

				headerColumns.push(element);
				if (and(!element.properties?.hide, !element.properties?.hidden)) {
					currentColumns.push(buildColumn(element, and(isGrouping, !noBordered, needBorderRight, !isLastElement)));
				}
				return { field: `${element._id}` };
			});
		};

		const columnsGrouping = getColumnGroupingModel(data, false, true);

		setIsColumnGrouping(isGrouping);
		return { columnsGrouping, currentColumns, headerColumns };
	};

	const pushRow = ({ styles, isFooter, item, currentColumns, row, rowHierarchy, currentRows }) => {
		item.cells?.forEach((cell) => {
			const columnAux = currentColumns?.find((columnElem) => columnElem._id.toString() === cell.columnId.toString());

			let cellStylesAux = cell.styles ?? [];
			let removeRowProperties = false;
			if (and(isFooter, cell.properties?.noFooter)) {
				removeRowProperties = true;
				cellStylesAux = {
					fontSize: "12px",
					fontFamily: "PTSansProNarrow",
					fontWeight: 400,
					color: "var(--text-color)",
					backgroundColor: "transparent",
					...cellStylesAux,
				};
			}

			if (columnAux) {
				row[`${columnAux._id}`] = {
					removeRowProperties,
					cellValue: cell.value,
					properties: cell.properties ?? [],
					styles: cellStylesAux,
					classes: cell.classes,
					noEditable: cell.noEditable,
					columnEdit: columnAux.columnEdit,
					decimals: columnAux.decimals ?? columnAux.decimal,
				};
			}

			const length = Math.max(rowHierarchy?.length - 2, 0);
			row["styles"] = { ...item.styles, ...styles, marginLeft: `${length * 10}px` };
		});

		if (rowHierarchy?.length) {
			row.hierarchy = rowHierarchy;
		}
		currentRows.push(row);
	};

	const handleHeaderCase = ({ column, item, row, currentColumns, rowHierarchy }) => {
		if (column) {
			row[`${column._id}`] = {
				cellValue: item.sectionTitle,
				properties: item.properties ?? [],
				styles: item.styles ?? [],
			};
		}
		if (item.lineNumber) {
			row[`${currentColumns?.[0]._id}`] = {
				cellValue: item.lineNumber,
			};
		}
		if (rowHierarchy?.length) {
			row.hierarchy = rowHierarchy;
		}
		row["rowProperties"] = { "is-row-header": true };
		if (item.collapsible) {
			row["styles"] = { marginLeft: `${(rowHierarchy?.length - 1) * 10}px` };
		}
		return row;
	};

	const deriveCurrentRows = ({ data, hierarchy, currentColumns }) => {
		let currentRows = [];
		data?.forEach((item) => {
			let row = {
				_id: item._id,
				noBordered: item.properties?.noBordered,
			};
			const rowHierarchy = hierarchy.concat([row._id]);

			const column = currentColumns[1] ?? currentColumns?.[0];
			const cellStyles = item?.["cellStyles"] ?? { fontSize: "14px", fontFamily: "Montserrat", fontWeight: 500 };
			switch (item.sectionType) {
				case "HEADER":
					row = handleHeaderCase({ column, item, row, currentColumns, rowHierarchy });
					currentRows.push(row);
					currentRows = currentRows.concat(
						buildRows(item.content, currentColumns, item.collapsible ? rowHierarchy : rowHierarchy.slice(0, -1))
					);
					break;
				case "FOOTER":
					row["rowProperties"] = {
						"is-row-footer": true,
						"edited-row-footer": !!props?.content?.tableInformation?.multipleEdition,
					};
					pushRow({ styles: cellStyles, isFooter: true, item, currentColumns, row, rowHierarchy, currentRows });
					break;
				case "SUB_FOOTER":
					row["rowProperties"] = { "is-row-sub-footer": true };
					pushRow({ styles: cellStyles, isFooter: true, item, currentColumns, row, rowHierarchy, currentRows });
					break;
				case "ROW":
					pushRow({ styles: { fontSize: "12px" }, isFooter: false, item, currentColumns, row, rowHierarchy, currentRows });
					if (item.collapsible) {
						currentRows = currentRows.concat(
							buildRows(item.content, currentColumns, item.collapsible ? rowHierarchy : rowHierarchy.slice(0, -1))
						);
					}
					break;
				default:
					break;
			}
		});
		return currentRows;
	};

	const buildRows = (data, currentColumns, hierarchy = []) => {
		const currentRows = deriveCurrentRows({ data, hierarchy, currentColumns });
		const longestArray = currentRows.map((row) => row.hierarchy).reduce((acc, curr) => (curr.length > acc.length ? curr : acc), []);
		setCollapseDepth(longestArray.length - 1);
		return currentRows;
	};

	const groupingColDef = {
		headerName: "",
		width: props?.content?.properties?.columnExpandWidth ?? defaultWidth,
		minWidth: props?.content?.properties?.columnExpandWidth ?? defaultWidth,
		headerClassName: "group-hide-span",
		cellClassName: ({ row }) => {
			const properties = row?.[1]?.removeRowProperties ? [] : row.rowProperties ?? [];
			return clsx({ ...properties, "group-hide-span": true, "background-white": row?.styles?.backgroundColor === "white" });
		},
	};

	const handleLeftClick = ({ allColumns, nextColumnIndex, coords }) => {
		for (let i = nextColumnIndex - 1; i >= 0; i--) {
			if (allColumns[i].isStep) {
				nextColumnIndex = i;
				break;
			}
		}
		return {
			...coords,
			colIndex: Math.max(0, nextColumnIndex === coords.colIndex ? 0 : nextColumnIndex),
			colNumber: Math.max(coords.colNumber - 1, 0),
		};
	};

	const handleRightClick = ({ allColumns, nextColumnIndex, coords }) => {
		for (let i = nextColumnIndex + 1; i < allColumns.length; i++) {
			if (allColumns[i].isStep) {
				nextColumnIndex = i;
				break;
			}
		}
		return {
			...coords,
			colIndex: nextColumnIndex,
			colNumber: Math.min(coords.colNumber + 1, totalSteps),
		};
	};

	const handleClick = (position) => () => {
		const allColumns = apiRef.current?.getAllColumns();
		setCoordinates((coords) => {
			const nextColumnIndex = coords.colIndex;
			if (position === "left") {
				return handleLeftClick({ allColumns, nextColumnIndex, coords });
			} else if (position === "right") {
				return handleRightClick({ allColumns, nextColumnIndex, coords });
			}
			return undefined;
		});
	};

	const handleNavigation = useCallback(() => {
		if (props?.content?.properties?.isScrollParent) {
			const newScrollCoord = apiRef.current.getScrollPosition();
			if (props?.changeTopScroll) {
				props.changeTopScroll(newScrollCoord.top);
			}
		}
	}, [apiRef, props]);

	const buildMultiInputData = (rowData) => {
		const editArray = [];
		const inputData = {
			locationId: props.parentsProps.locationId,
			commissionCycleId: props.parentsProps.commissionCycleId,
			franchise: props.parentsProps.franchise,
			oemReference: props.parentsProps.oemReference,
			dealerCodeFS: props.parentsProps.dealerCodeFS,
			metrics: [],
		};

		Object.values(rowData).forEach((cell) => {
			if (and(typeof cell === "object", cell?.input)) {
				const editObject = {
					parents: cell.input.parents,
					update: {
						value: Number(cell.input.value),
						columnId: cell.input.columnId,
					},
				};
				editArray.push(editObject);
			}
		});

		inputData.metrics.push(...editArray);
		return inputData;
	};

	const updateCallback = (rowId, newData) => {
		const time = 2000;
		setTimeout(() => {
			RowFeedback.skeleton(rowId, apiRef).success();
			const updatedRow = { id: rowId, ...newData };
			apiRef.current.updateRows([updatedRow]);
		}, time);

		return true;
	};

	const handleUpdate = (inputData, rowId, rowData) => {
		if (inputData?.metrics?.length) {
			apiRef.current.stopRowEditMode({ id: rowId });
			removeActionButtons();
			requestAnimationFrame(() => RowFeedback.skeleton(rowId, apiRef).display());
			props.onUpdateCellTableValue(
				inputData,
				() => updateCallback(rowId, rowData),
				() => console.error("Something went wrong.")
			);
		}
	};

	const handleCellDoubleClick = (params) => {
		const rowId = params.id;
		if (!originalRowDataRef.current[rowId]) {
			originalRowDataRef.current[rowId] = JSON.parse(JSON.stringify(params.row));
		}

		const actionColumn = {
			field: "actions",
			renderCell: () => (
				<Grid>
					<SaveWhiteIconComponent
						onClick={() => {
							const rowData = params.row;
							const inputData = buildMultiInputData(rowData);
							handleUpdate(inputData, rowId, rowData);
							resetOriginalDataInputs(rowId);
						}}
						className="ml-6 mr-3"
						style={{ cursor: "pointer" }}
					/>
					<CancelIcon
						onClick={() => {
							restoreOriginalDataInputs(rowId);
							apiRef.current.stopRowEditMode({ id: rowId });
							removeActionButtons();
							resetOriginalDataInputs(rowId);
						}}
						style={{ cursor: "pointer" }}
					/>
				</Grid>
			),
		};
		setColumns((prev) => {
			const newColumns = [...prev];
			const lastColumn = {
				...newColumns[newColumns.length - 1],
				renderCell: actionColumn.renderCell,
				field: "actions",
			};
			newColumns[newColumns.length - 1] = lastColumn;
			return newColumns;
		});
	};

	const restoreOriginalDataInputs = (rowId) => {
		const originalData = originalRowDataRef.current[rowId];
		Object.keys(originalData).forEach((field) => {
			apiRef.current.setEditCellValue({ field, id: rowId, value: originalData[field] });
		});
	};

	const resetOriginalDataInputs = (rowId) => (originalRowDataRef.current[rowId] = null);

	const removeActionButtons = () => {
		setColumns((prev) => {
			return prev.map((column) => {
				if (column.field === "actions") {
					return { ...column, renderCell: null };
				}
				return column;
			});
		});
	};

	const onCellDoubleClick = getOnCellDoubleClickHandler(props?.content?.tableInformation?.multipleEdition, handleCellDoubleClick);

	useEffect(() => {
		if (and(isNumeric(props?.topScrollValue), props?.content?.properties?.isScrollChild)) {
			try {
				apiRef.current.scroll({ top: props.topScrollValue });
			} catch {
				return undefined;
			}
		}
		return () => {};
	}, [apiRef, props?.topScrollValue, props?.content?.properties?.isScrollChild]);

	useEffect(() => {
		if (props?.content?.properties?.scrollBySteps) {
			const allColumns = apiRef.current?.getAllColumns();

			let scrollLeft = -1;
			for (let i = 0; i < coordinates.colIndex; i++) {
				scrollLeft += allColumns[i].computedWidth;
			}

			try {
				apiRef.current.scroll({ left: scrollLeft });
			} catch {
				return;
			}
		}
	}, [apiRef, coordinates, props?.content?.properties?.scrollBySteps]);

	useEffect(() => {
		if (and(props?.content?.properties?.isScrollParent, apiRef?.current.subscribeEvent)) {
			apiRef.current.subscribeEvent("scrollPositionChange", handleNavigation);
		}
	}, [apiRef, props?.content?.properties?.isScrollParent, handleNavigation]);

	useEffect(() => {
		if (props) {
			const { columnsGrouping, currentColumns, headerColumns } = buildColumnGroupingModel(props.content.columns);
			setColumns(currentColumns);
			setColumnGroupingModel(columnsGrouping);
			setRows(buildRows(props.content.content, headerColumns));
			setTotalSteps(currentColumns.filter((column) => column.isStep).length);
		}

		return () => {
			setColumns([]);
			setColumnGroupingModel([]);
			setRows([]);
			setTotalSteps(0);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props]);

	return (
		<>
			<DataSmartTable
				apiRef={apiRef}
				columns={columns}
				getRowHeight={({ model }) => model?.styles?.height ?? DEFAULT_ROW_HEIGHT}
				columnGroupingModel={columnGroupingModel}
				experimentalFeatures={{ columnGrouping: isColumnGrouping }}
				rows={rows}
				treeData
				getTreeDataPath={getTreeDataPath}
				groupingColDef={groupingColDef}
				defaultGroupingExpansionDepth={collapseDepth}
				headerHeight={props?.content?.properties?.headerHeight ?? DEFAULT_HEADER_HEIGHT}
				disableHScrollCorrection
				disableColumnMenu
				disableRowSelectionOnClick
				disableMultipleRowSelection={true}
				disableColumnReorder={true}
				checkboxSelection={false}
				autoHeight={!props?.content?.styles?.height}
				isCellEditable={({ formattedValue }) => !formattedValue?.noEditable}
				editMode={editMode}
				onCellDoubleClick={onCellDoubleClick}
				hideFooter
				maxRows={maxRows}
				disableHeightRounding={props?.tableProps?.disableHeightRounding ?? true}
				{...props?.tableProps}
				customProps={{
					tableContainerStyle: props?.tableProps?.customProps?.tableContainerStyle ?? { height: "100%", width: "100%" },
					tableClassName: props?.tableProps?.customProps?.tableClassName ?? {
						"functional-table": true,
						// styles to fix overlapping horizontal scrollbar
						"[&_.MuiDataGrid-scrollbar--horizontal]:absolute": true,
						"[&_.MuiDataGrid-main]:overflow-visible": !props?.content?.properties?.scrollBySteps,
						"[&_.MuiDataGrid-scrollbar--horizontal]:bottom-[-10px]": !props?.content?.properties?.isScrollChild,
						// styles to hide default horizontal scrollbar for scrollBySteps
						"[&_.MuiDataGrid-scrollbar--horizontal]:hidden": props?.content?.properties?.scrollBySteps,
						// styles for isScrollChild
						"pb-[50px]": props?.content?.properties?.isScrollChild,
						"[&_.MuiDataGrid-main_.MuiDataGrid-virtualScroller]:overflow-hidden": props?.content?.properties?.isScrollChild,
						"[&_.MuiDataGrid-scrollbar--vertical]:hidden": props?.content?.properties?.isScrollChild,
						"[&_.MuiDataGrid-scrollbar--horizontal]:bottom-[-30px]": props?.content?.properties?.isScrollChild,
						"[&_.MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar-thumb]:!rounded-none":
							props?.content?.properties?.isScrollChild,
						"[&_.MuiDataGrid-scrollbar--horizontal::-webkit-scrollbar-track]:!rounded-none":
							props?.content?.properties?.isScrollChild,
					},
					expandableRows: props?.tableProps?.customProps?.expandableRows ?? false,
					hide: props?.tableProps?.customProps?.hide ?? false,
				}}
			/>
			{props?.content?.properties?.scrollBySteps && (
				<Grid className="horizontal-scrolling" marginTop={1}>
					<Grid width={60}>
						<Button className="horizontal-scrolling-button" onClick={handleClick("left")}>
							<img src={leftArrowIcon} alt="icon" />
						</Button>
						<Button className="horizontal-scrolling-button" onClick={handleClick("right")}>
							<img src={rightArrowIcon} alt="icon" />
						</Button>
					</Grid>
					<Grid paddingX={2} flex={1}>
						<Slider value={coordinates.colNumber} step={1} max={totalSteps + 1} />
					</Grid>
				</Grid>
			)}
		</>
	);
}

import PropTypes from "prop-types";
OemDataTable.propTypes = {
	tableProps: PropTypes.object,
	topScrollValue: PropTypes.number,
	parentsProps: PropTypes.object,
	changeTopScroll: PropTypes.func,
	onUpdateCellTableValue: PropTypes.func,
	content: {
		columns: PropTypes.array,
		content: PropTypes.array,
		styles: PropTypes.object,
		properties: {
			columnExpandWidth: PropTypes.number,
			scrollBySteps: PropTypes.bool,
			isScrollParent: PropTypes.bool,
			isScrollChild: PropTypes.bool,
			headerHeight: PropTypes.number,
		},
	},
};
